// DSR Admin — single user account: edit profile, link a person, assign roles,
// run account actions (reset password, revoke sessions, force change, activate/
// deactivate, delete), and view effective permissions. Gated on
// settings.manage_users (the whole DSR Admin nav group). Server enforces the
// self-lockout and last-admin guardrails; the UI also disables self-destructive
// actions.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatPhoneDisplay } from '@dsr/shared-types';
import {
  useAdminUser,
  useUpdateAdminUser,
  useSetAdminUserRoles,
  useSetAdminUserPerson,
  useResetAdminUserPassword,
  useRevokeAdminUserSessions,
  useDeleteAdminUser,
  useRoles,
} from '../api/queries/admin';
import { PhoneInput } from '../components/PhoneInput';
import { PersonPicker } from '../components/PersonPicker';
import { MailboxCard } from '../components/MailboxCard';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  StatusBadge,
} from '../components/ui';
import { ApiError } from '../api/client';
import { useAuthStore, can } from '../auth/store';
import { fmtDateTime } from '../utils/date';

export function AdminUserDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const userId = id ? Number(id) : null;
  const navigate = useNavigate();
  const meId = useAuthStore((s) => s.me?.user.id ?? null);
  const isSelf = meId !== null && meId === userId;

  const { data, isLoading, isError, error } = useAdminUser(userId);
  const { data: rolesData } = useRoles();
  const allRoles = rolesData?.roles ?? [];

  if (isLoading) return <p className="muted">Loading…</p>;
  if (isError || !data) {
    return (
      <p className="error-text" role="alert">
        {error instanceof ApiError ? error.message : 'Failed to load user.'}
      </p>
    );
  }

  const user = data.user;

  return (
    <div className="page admin-user-detail-page">
      <nav className="breadcrumbs">
        <Link to="/admin/users">User Accounts</Link>
        <span aria-hidden="true"> / </span>
        <span>{user.display_name}</span>
      </nav>

      <header className="page-header">
        <div>
          <h1>{user.display_name}</h1>
          <p className="page-sub">{user.email}</p>
        </div>
        <div className="header-actions">
          {user.is_active
            ? <Badge tone="success">Active</Badge>
            : <Badge tone="neutral">Inactive</Badge>}
          {user.is_2fa_enabled && <StatusBadge status="new">2FA</StatusBadge>}
          {user.must_change_password && <StatusBadge status="new">Pwd reset pending</StatusBadge>}
        </div>
      </header>

      <ProfileCard user={user} />
      <PersonLinkCard userId={user.id} personId={user.person_id} personName={user.person_name} />

      {can('mailbox.manage') && (
        user.person_id !== null ? (
          <MailboxCard
            personId={user.person_id}
            hasPrimaryPersonalEmail={user.person_has_primary_personal_email}
          />
        ) : (
          <section className="detail-card">
            <h2>Company mailbox</h2>
            <p className="muted">Link this account to a person to provision a mailbox.</p>
          </section>
        )
      )}

      <RolesCard userId={user.id} assigned={user.roles.map((r) => r.id)} all={allRoles} />
      <ActionsCard
        userId={user.id}
        isActive={user.is_active}
        isSelf={isSelf}
        onDeleted={() => navigate('/admin/users')}
      />

      <section className="detail-card">
        <h2>Effective permissions</h2>
        {user.effective_permissions.length === 0 ? (
          <p className="muted">No permissions (no roles assigned).</p>
        ) : (
          <div className="tag-list">
            {user.effective_permissions.map((p) => (
              <span key={p} className="tag tag--steel mono">{p}</span>
            ))}
          </div>
        )}
        <p className="muted small">Last sign-in: {fmtDateTime(user.last_login_at)}</p>
      </section>
    </div>
  );
}

// ---------- Profile ----------

function ProfileCard({
  user,
}: {
  user: import('@dsr/shared-types').AdminUserDetail;
}): JSX.Element {
  const update = useUpdateAdminUser();
  const [displayName, setDisplayName] = useState(user.display_name);
  const [email, setEmail] = useState(user.email);
  const [phone, setPhone] = useState(user.phone ? formatPhoneDisplay(user.phone) : '');

  const onSave = async (): Promise<void> => {
    try {
      await update.mutateAsync({
        id: user.id,
        display_name: displayName.trim(),
        email: email.trim(),
        phone: phone.trim() ? phone.trim() : null,
      });
      toast.success('Profile updated.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Update failed.');
    }
  };

  return (
    <section className="detail-card">
      <h2>Profile</h2>
      <div className="form-grid">
        <FormField label="Display name">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </FormField>
        <FormField label="Email">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label="Phone">
          <PhoneInput value={phone} onChange={setPhone} />
        </FormField>
      </div>
      <div className="settings-actions">
        <Button variant="primary" disabled={update.isPending} onClick={() => void onSave()}>
          {update.isPending ? 'Saving…' : 'Save profile'}
        </Button>
      </div>
    </section>
  );
}

// ---------- Person link ----------

function PersonLinkCard({
  userId,
  personId,
  personName,
}: {
  userId: number;
  personId: number | null;
  personName: string | null;
}): JSX.Element {
  const setPerson = useSetAdminUserPerson();
  const [picking, setPicking] = useState(false);

  const link = async (pid: number | null): Promise<void> => {
    try {
      await setPerson.mutateAsync({ id: userId, person_id: pid });
      toast.success(pid === null ? 'Person unlinked.' : 'Person linked.');
      setPicking(false);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update link.');
    }
  };

  return (
    <section className="detail-card">
      <h2>Linked person</h2>
      <p>
        {personId !== null
          ? <>Linked to <strong>{personName ?? `person #${personId}`}</strong>.</>
          : 'Not linked to a person in the registry.'}
      </p>
      <div className="settings-actions">
        <button type="button" className="btn btn-secondary btn-sm" disabled={setPerson.isPending} onClick={() => setPicking(true)}>
          {personId !== null ? 'Change' : 'Link person'}
        </button>
        {personId !== null && (
          <Button variant="ghost" className="btn-sm" disabled={setPerson.isPending} onClick={() => void link(null)}>
            Unlink
          </Button>
        )}
      </div>
      {picking && (
        <Dialog title="Link to person" open onClose={() => setPicking(false)} busy={setPerson.isPending}>
          <PersonPicker onSelect={(p) => void link(p.id)} />
        </Dialog>
      )}
    </section>
  );
}

// ---------- Roles ----------

function RolesCard({
  userId,
  assigned,
  all,
}: {
  userId: number;
  assigned: number[];
  all: import('@dsr/shared-types').RoleListItem[];
}): JSX.Element {
  const setRoles = useSetAdminUserRoles();
  const [selected, setSelected] = useState<number[]>(assigned);

  // Re-seed when the server state changes (e.g. after a save invalidates).
  useEffect(() => {
    setSelected(assigned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned.join(',')]);

  const toggle = (id: number): void =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((r) => r !== id) : [...cur, id]));

  const dirty =
    selected.length !== assigned.length || selected.some((id) => !assigned.includes(id));

  const onSave = async (): Promise<void> => {
    try {
      await setRoles.mutateAsync({ id: userId, role_ids: selected });
      toast.success('Roles updated.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update roles.');
    }
  };

  return (
    <section className="detail-card">
      <h2>Roles</h2>
      <div className="checkbox-list">
        {all.map((r) => (
          <label key={r.id} className="checkbox-field" title={r.description ?? undefined}>
            <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggle(r.id)} />
            <span>{r.name}</span>
          </label>
        ))}
        {all.length === 0 && <span className="muted small">No roles defined.</span>}
      </div>
      <div className="settings-actions">
        <Button variant="primary" disabled={!dirty || setRoles.isPending} onClick={() => void onSave()}>
          {setRoles.isPending ? 'Saving…' : 'Save roles'}
        </Button>
      </div>
    </section>
  );
}

// ---------- Account actions ----------

function ActionsCard({
  userId,
  isActive,
  isSelf,
  onDeleted,
}: {
  userId: number;
  isActive: boolean;
  isSelf: boolean;
  onDeleted: () => void;
}): JSX.Element {
  const update = useUpdateAdminUser();
  const reset = useResetAdminUserPassword();
  const revoke = useRevokeAdminUserSessions();
  const del = useDeleteAdminUser();

  const [resetOpen, setResetOpen] = useState(false);
  const [resetMode, setResetMode] = useState<'email' | 'set'>('email');
  const [resetPw, setResetPw] = useState('');
  const [resetForce, setResetForce] = useState(true);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  const setActive = async (active: boolean): Promise<void> => {
    try {
      await update.mutateAsync({ id: userId, is_active: active });
      toast.success(active ? 'Account activated.' : 'Account deactivated.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to update status.');
    }
  };

  const forceChange = async (): Promise<void> => {
    try {
      await update.mutateAsync({ id: userId, must_change_password: true });
      toast.success('User must change their password at next sign-in.');
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const doReset = async (): Promise<void> => {
    try {
      const res = await reset.mutateAsync({
        id: userId,
        ...(resetMode === 'set' ? { password: resetPw } : {}),
        must_change_password: resetForce,
      });
      setResetOpen(false);
      setResetPw('');
      if (res.temp_password) {
        setTempPassword(res.temp_password);
      } else if (res.emailed) {
        toast.success('Password reset. A temporary password was emailed.');
      } else {
        toast.success('Password updated.');
      }
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Reset failed.');
    }
  };

  const doRevoke = async (): Promise<void> => {
    try {
      const res = await revoke.mutateAsync(userId);
      toast.success(`Revoked ${res.revoked} session(s).`);
      setConfirmRevoke(false);
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Revoke failed.');
    }
  };

  const doDelete = async (): Promise<void> => {
    try {
      await del.mutateAsync(userId);
      toast.success('Account deleted.');
      onDeleted();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Delete failed.');
      setConfirmDelete(false);
    }
  };

  const busy = update.isPending || reset.isPending || revoke.isPending || del.isPending;

  return (
    <section className="detail-card">
      <h2>Account actions</h2>
      <div className="settings-actions wrap">
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setResetOpen(true)}>
          Reset password
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void forceChange()}>
          Force change on next login
        </button>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setConfirmRevoke(true)}>
          Revoke all sessions
        </button>
        {isActive ? (
          <Button
            variant="ghost"
            className="btn-sm"
            disabled={busy || isSelf}
            title={isSelf ? 'You cannot deactivate your own account' : undefined}
            onClick={() => void setActive(false)}
          >
            Deactivate
          </Button>
        ) : (
          <Button variant="ghost" className="btn-sm" disabled={busy} onClick={() => void setActive(true)}>
            Activate
          </Button>
        )}
        <Button
          variant="danger"
          className="btn-sm"
          disabled={busy || isSelf}
          title={isSelf ? 'You cannot delete your own account' : undefined}
          onClick={() => setConfirmDelete(true)}
        >
          Delete
        </Button>
      </div>

      {resetOpen && (
        <Dialog
          title="Reset password"
          open
          onClose={() => setResetOpen(false)}
          busy={reset.isPending}
          footer={
            <>
              <Button variant="ghost" onClick={() => setResetOpen(false)} disabled={reset.isPending}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={reset.isPending || (resetMode === 'set' && resetPw.length < 12)}
                onClick={() => void doReset()}
              >
                {reset.isPending ? 'Working…' : 'Reset'}
              </Button>
            </>
          }
        >
          <p className="muted small">Resetting signs the user out of every device.</p>
          <div className="checkbox-list">
            <label className="checkbox-field">
              <input type="radio" name="reset-mode" checked={resetMode === 'email'} onChange={() => setResetMode('email')} />
              <span>Email a temporary password</span>
            </label>
            <label className="checkbox-field">
              <input type="radio" name="reset-mode" checked={resetMode === 'set'} onChange={() => setResetMode('set')} />
              <span>Set a password</span>
            </label>
          </div>
          {resetMode === 'set' && (
            <Input
              type="password"
              placeholder="At least 12 characters"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              minLength={12}
              autoComplete="new-password"
            />
          )}
          <label className="checkbox-field">
            <input type="checkbox" checked={resetForce} onChange={(e) => setResetForce(e.target.checked)} />
            <span>Require password change on next login</span>
          </label>
        </Dialog>
      )}
      <ConfirmDialog
        open={confirmRevoke}
        title="Revoke all sessions"
        message="Sign this user out of every device immediately?"
        confirmLabel="Revoke"
        busy={revoke.isPending}
        onConfirm={() => void doRevoke()}
        onCancel={() => setConfirmRevoke(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="Delete account"
        message="Soft-delete this account? The user can no longer sign in. This can be undone by a database administrator."
        confirmLabel="Delete"
        danger
        busy={del.isPending}
        onConfirm={() => void doDelete()}
        onCancel={() => setConfirmDelete(false)}
      />

      {tempPassword !== null && (
        <Dialog
          title="Temporary password"
          open
          onClose={() => setTempPassword(null)}
          footer={<Button variant="primary" onClick={() => setTempPassword(null)}>Done</Button>}
        >
          <p>Email could not be sent. Share this temporary password securely — the user must change it at next sign-in:</p>
          <code className="mono temp-password">{tempPassword}</code>
        </Dialog>
      )}
    </section>
  );
}
